import React, { useState, useCallback, useEffect } from 'react';
import { Search, X, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SearchPill {
  id: string;
  text: string;
  operator: 'OR' | 'AND' | 'NOT';
}

interface SearchFilterWithPillsProps {
  onSearch: (terms: string[], operators: {[key: string]: 'OR' | 'AND' | 'NOT'}) => void;
  placeholder?: string;
  initialPills?: SearchPill[];
  resetTrigger?: number; // Nuevo prop para resetear el componente
  hasExternalChanges?: boolean; // Nuevo prop para indicar cambios en otros filtros
  externalOperators?: {[key: string]: 'OR' | 'AND' | 'NOT'}; // Operadores externos para sincronizar
}

const SearchFilterWithPills: React.FC<SearchFilterWithPillsProps> = ({
  onSearch,
  placeholder = "Buscar productos...",
  initialPills = [],
  resetTrigger,
  hasExternalChanges = false,
  externalOperators = {}
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [searchPills, setSearchPills] = useState<SearchPill[]>(initialPills);
  const [hasPendingChanges, setHasPendingChanges] = useState(false); // Inicialmente deshabilitado
  const [lastAppliedState, setLastAppliedState] = useState<{pills: SearchPill[], input: string}>({pills: [], input: ''});
  const [showTooltip, setShowTooltip] = useState(false);

  const generatePillId = () => `pill_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Resetear el componente cuando cambie resetTrigger
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      // Usar initialPills si están disponibles
      setSearchPills(initialPills);
      setSearchInput('');
      setLastAppliedState({pills: initialPills, input: ''});
      setHasPendingChanges(initialPills.length > 0);
    }
  }, [resetTrigger, initialPills]);

  // Sincronizar operadores externos
  useEffect(() => {
    if (Object.keys(externalOperators).length > 0) {
      setSearchPills(prev => prev.map(pill => {
        const externalOperator = externalOperators[pill.text];
        if (externalOperator && externalOperator !== pill.operator) {
          return { ...pill, operator: externalOperator };
        }
        return pill;
      }));
    }
  }, [externalOperators]);

  // Detectar cambios para habilitar/deshabilitar el botón
  useEffect(() => {
    // Comparar el estado actual con el último estado aplicado
    const currentState = {
      pills: searchPills,
      input: searchInput.trim()
    };
    
    // Crear un string de comparación para cada estado
    const currentStateString = JSON.stringify({
      pills: currentState.pills.map(p => ({ text: p.text, operator: p.operator })).sort((a, b) => a.text.localeCompare(b.text)),
      input: currentState.input
    });
    
    const lastAppliedStateString = JSON.stringify({
      pills: lastAppliedState.pills.map(p => ({ text: p.text, operator: p.operator })).sort((a, b) => a.text.localeCompare(b.text)),
      input: lastAppliedState.input
    });
    
    // Comparación más robusta usando serialización
    const hasInternalChanges = currentStateString !== lastAppliedStateString;
    
    // El botón está habilitado si hay cambios internos O cambios externos
    const hasAnyChanges = hasInternalChanges || hasExternalChanges;
    
    
    if (!hasAnyChanges) {
    }
    
    setHasPendingChanges(hasAnyChanges);
    
  }, [searchPills, searchInput, lastAppliedState, hasExternalChanges]);

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && searchInput.trim()) {
      e.preventDefault();
      createPillFromInput();
    }
  };

  const createPillFromInput = () => {
    const text = searchInput.trim();
    if (!text) return;

    // Detectar si empieza con "-" para NOT automático
    let cleanText = text;
    let operator: 'OR' | 'AND' | 'NOT' = 'AND'; // Por defecto siempre AND
    
    if (text.startsWith('-')) {
      cleanText = text.substring(1).trim();
      operator = 'NOT';
    }

    if (!cleanText) return;

    // Verificar si ya existe una pill con el mismo texto
    const existingPill = searchPills.find(pill => pill.text.toLowerCase() === cleanText.toLowerCase());
    if (existingPill) {
      setSearchInput('');
      return;
    }

    const newPill: SearchPill = {
      id: generatePillId(),
      text: cleanText,
      operator: operator
    };

    setSearchPills(prev => [...prev, newPill]);
    setSearchInput('');
  };

  const togglePillOperator = (pillId: string) => {
    setSearchPills(prev => {
      const newPills = prev.map(pill => {
        if (pill.id === pillId) {
          const nextOperator: 'OR' | 'AND' | 'NOT' = pill.operator === 'OR' ? 'AND' : pill.operator === 'AND' ? 'NOT' : 'OR';
          return { ...pill, operator: nextOperator };
        }
        return pill;
      });
      return newPills;
    });
  };

  const removePill = (pillId: string) => {
    setSearchPills(prev => {
      const filtered = prev.filter(pill => pill.id !== pillId);
      
      // Si después de eliminar queda solo un término y no es NOT, cambiarlo a AND
      if (filtered.length === 1 && filtered[0].operator !== 'NOT') {
        return filtered.map(pill => ({
          ...pill,
          operator: 'AND'
        }));
      }
      
      return filtered;
    });
  };

  const clearAllSearchPills = () => {
    setSearchPills([]);
    setSearchInput('');
  };

  const applyFilters = useCallback(() => {
    
    // Si hay texto en el input que no es un pill, créalo primero
    const trimmedInput = searchInput.trim();
    let allPills = [...searchPills];
    
    if (trimmedInput) {
      // Crear un pill temporal para el texto del input
      let operator: 'OR' | 'AND' | 'NOT' = 'AND'; // Por defecto siempre AND
      let cleanText = trimmedInput;
      
      if (trimmedInput.startsWith('-')) {
        cleanText = trimmedInput.substring(1).trim();
        operator = 'NOT';
      }
      
      if (cleanText && !allPills.find(pill => pill.text.toLowerCase() === cleanText.toLowerCase())) {
        const tempPill: SearchPill = {
          id: generatePillId(),
          text: cleanText,
          operator: operator
        };
        allPills.push(tempPill);
        
        // Actualizar el estado para que el pill se muestre
        setSearchPills([...searchPills, tempPill]);
        setSearchInput('');
      }
    }
    
    const terms = allPills.map(pill => pill.text);
    const operators = allPills.reduce((acc, pill) => {
      acc[pill.text] = pill.operator;
      return acc;
    }, {} as {[key: string]: 'OR' | 'AND' | 'NOT'});

    try {
      onSearch(terms, operators);
    } catch (error) {
      console.error('🚨🚨🚨 ERROR AL EJECUTAR onSearch:', error);
    }
    
    // Guardar el estado actual como el último aplicado
    setLastAppliedState({
      pills: [...allPills],
      input: ''
    });
    setHasPendingChanges(false);
  }, [searchPills, searchInput, onSearch, generatePillId]);

  const operatorColors = {
    'OR': 'bg-blue-100 text-blue-800 border-blue-200',
    'AND': 'bg-green-100 text-green-800 border-green-200', 
    'NOT': 'bg-red-100 text-red-800 border-red-200'
  };

  return (
    <div className="space-y-4">
      {/* Campo de búsqueda con pills internas */}
      <div>
        <div className="flex items-center mb-2">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            {/* PT-#45 #16 — ícono a la izquierda del label, concordante con el resto de la caja. */}
            <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            {t('filters.global_search')}
          </label>
          <div className="relative ml-2">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
            >
              <Info className="h-4 w-4" />
            </button>
            {showTooltip && (
              <div className="absolute left-6 top-0 z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                <div className="space-y-1">
                  <p>• {t('filters.tooltip.press_tab')}</p>
                  <p>• {t('filters.tooltip.or_operator')}</p>
                  <p>• {t('filters.tooltip.and_operator')}</p>
                  <p>• {t('filters.tooltip.not_operator')}</p>
                  <p>• {t('filters.tooltip.auto_not')}</p>
                </div>
                {/* Flecha del tooltip */}
                <div className="absolute left-0 top-2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900 -translate-x-1"></div>
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="min-h-[80px] border border-gray-300 rounded-lg p-3 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            {/* Pills existentes */}
            {searchPills.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {searchPills.map((pill) => (
                  <span key={pill.id} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${operatorColors[pill.operator]}`}>
                    <button
                      onClick={() => togglePillOperator(pill.id)}
                      className="mr-1 font-bold hover:opacity-70 transition-opacity"
                      title={t('filters.tooltip.change_operator', { operator: pill.operator })}
                    >
                      {pill.operator}
                    </button>
                    <span className="mx-1">"{pill.text}"</span>
                    <button
                      onClick={() => removePill(pill.id)}
                      className="ml-1 hover:opacity-70 transition-opacity"
                      title={t('filters.tooltip.remove_term', { term: pill.text })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Campo de entrada */}
                                    <textarea
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={handleSearchInputKeyDown}
                          className="w-full resize-none border-none outline-none bg-transparent text-sm placeholder-gray-400"
                          placeholder={searchPills.length === 0 ? placeholder : t('filters.tooltip.more_terms_placeholder')}
                          rows={2}
                        />
          </div>
          
          {/* Iconos de ayuda */}
          <div className="absolute right-3 top-3 flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-400" />
            {searchPills.length > 0 && (
              <button
                onClick={clearAllSearchPills}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title={t('filters.tooltip.clear_all_pills')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Botón Filtrar */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            applyFilters();
          }}
          disabled={!hasPendingChanges}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            hasPendingChanges 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {t('filters.filter')}
        </button>
      </div>

    </div>
  );
};

export default SearchFilterWithPills; 